"use client";

import React, { useState, useEffect } from "react";
import {
  challengePaths,
  ChallengePath,
  ChallengeStage,
} from "@/lib/challengePaths";
import { motion, AnimatePresence } from "framer-motion";
import { ChallengePlayer } from "./ChallengePlayer";
import { FeedbackModal } from "./modals/FeedbackModal";
import { updateUserScore } from "@/lib/user/updateUserScore";
import { useAuth } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { markChallengeCompleted } from "@/lib/challenge/markChallengeCompleted";
import { CompletedChallengeType } from "@/app/@types";
import { getCurrentPathData } from "@/helpers/getCurrentPathData";
import { getUserCompletedChallenges } from "@/lib/challenge/getUserCompletedChallanges";
import { organizeCompletedChallengesByPath } from "@/helpers/organizeCompletedChallengeByPath";
import { addChallengeActivity } from "@/lib/activity/addActivityToDB";
import { toast } from "react-toastify";

export default function ChallengePathNavigator() {
  const { user } = useAuth();
  const {
    userScore,
    completedChallenges,
    setCompletedChallenges,
    updateUserPointsScore,
    setIsModalOpen,
    setActiveModal,
  } = useAppStore();

  const [selectedPath, setSelectedPath] = useState<ChallengePath | null>(null);
  const [activeStage, setActiveStage] = useState<ChallengeStage | null>(null);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [stageScore, setStageScore] = useState(0);
  const [isChallengeCompleted, setChallengeCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [completedChallengesDetailed, setCompletedChallengesDetailed] =
    useState<CompletedChallengeType[]>([]);

  useEffect(() => {
    if (challengePaths.length > 0) {
      setSelectedPath(challengePaths[0]);
    }
  }, []);

  useEffect(() => {
    const fetchCompletedChallenges = async () => {
      if (user?.id) {
        try {
          const dbCompletedChallenges = await getUserCompletedChallenges(
            user.id
          );

          const completedChallengesIds = dbCompletedChallenges.map(
            (challenge: any) => challenge.challenge_id
          );
          setCompletedChallenges(completedChallengesIds);

          const pathData = organizeCompletedChallengesByPath(
            dbCompletedChallenges,
            user.id
          );
          const actualScore =
            selectedPath && getPathTotalScore(selectedPath.id);

          if (!!actualScore) {
            setStageScore(actualScore);
          }
        } catch (error) {
          toast.error("Failed to fetch completed challenges");
          console.error("Failed to fetch completed challenges:", error);
        }
      } else {
        setCompletedChallenges([]);
        challengePaths.forEach((path) => {
          localStorage.removeItem(`path_${path.id}_${user?.id}`);
        });
      }
    };

    fetchCompletedChallenges();
  }, [currentChallengeIndex, selectedPath, activeStage, user?.id]);

  const getPathTotalScore = (pathId: string): number => {
    if (!user?.id) return 0;

    const pathData = getCurrentPathData(pathId, user.id);
    return pathData?.totalScore || 0;
  };

  const getStagePointsFromDB = (stage: ChallengeStage): number => {
    const stageChallengeIds = stage.challenges.map((c) => c.id);
    return completedChallengesDetailed
      .filter((completed) => stageChallengeIds.includes(completed.challenge_id))
      .reduce((total, completed) => total + completed.points_earned, 0);
  };

  const handleSelectStage = (stage: ChallengeStage) => {
    if (!user?.email) {
      setIsModalOpen(true);
      setActiveModal("auth");
    } else {
      const allCompleted = stage.challenges.every((challenge) =>
        isChallengeCompletedCheck(challenge.id)
      );

      if (allCompleted) {
        return;
      }

      const firstUncompletedIndex = stage.challenges.findIndex(
        (challenge) => !isChallengeCompletedCheck(challenge.id)
      );

      setActiveStage(stage);
      setCurrentChallengeIndex(
        firstUncompletedIndex >= 0 ? firstUncompletedIndex : 0
      );

      const existingStagePoints = getStagePointsFromDB(stage);
      setStageScore(existingStagePoints);
    }
  };

  const handleChallengeComplete = async (
    isCorrect: boolean,
    points: number
  ) => {
    try {
      if (activeStage && user?.id) {
        const currentChallenge = activeStage.challenges[currentChallengeIndex];

        await markChallengeCompleted(
          user.id,
          currentChallenge.id,
          isCorrect ? points : 0,
          false
        );
        if (!completedChallenges.includes(currentChallenge.id)) {
          setCompletedChallenges([...completedChallenges, currentChallenge.id]);

          if (isCorrect) {
            const newDetailedItem: CompletedChallengeType = {
              challenge_id: currentChallenge.id,
              points_earned: points,
              beat_crowd: false,
              completed_at: new Date().toISOString(),
            };
            setCompletedChallengesDetailed([
              newDetailedItem,
              ...completedChallengesDetailed,
            ]);
          }
        }
      }

      if (isCorrect) {
        setStageScore((prev) => prev + points);
      }

      if (activeStage) {
        const nextUncompletedIndex = activeStage.challenges.findIndex(
          (challenge, index) =>
            index > currentChallengeIndex &&
            !isChallengeCompletedCheck(challenge.id)
        );

        if (nextUncompletedIndex !== -1) {
          setCurrentChallengeIndex(nextUncompletedIndex);
        } else {
          const actualStagePoints =
            selectedPath && getPathTotalScore(selectedPath.id);
          const finalStagePoints =
            (actualStagePoints || 0) + (isCorrect ? points : 0);

          setChallengeCompleted(true);

          const newScore = userScore + finalStagePoints;
          updateUserPointsScore(finalStagePoints);
          await addChallengeActivity(finalStagePoints);

          if (user?.id) {
            try {
              await updateUserScore(user.id, finalStagePoints);
            } catch (error) {
              toast.error("Failed to update user score");
              console.error("Failed to update user score:", error);
            }
          }

          setFinalScore(finalStagePoints);
          setActiveStage(null);
        }
      }
    } catch (error) {
      toast.error("Failed to handle completed challenges");
      console.error("Failed to handle completed challenges:", error);
    }
  };

  const getFeedbackType = (score: number): "success" | "error" | "info" => {
    if (score >= 100) return "success";
    return "info";
  };

  const isChallengeCompletedCheck = (challengeId: string): boolean => {
    return completedChallenges.includes(challengeId);
  };

  const getStageCompletionStatus = (stage: ChallengeStage) => {
    const completedCount = stage.challenges.filter((challenge) =>
      isChallengeCompletedCheck(challenge.id)
    ).length;
    return {
      completed: completedCount,
      total: stage.challenges.length,
      isFullyCompleted: completedCount === stage.challenges.length,
    };
  };

  if (!challengePaths || challengePaths.length === 0) {
    return (
      <div className="p-8 text-white">
        <h2 className="text-3xl font-bold mb-8">Legendary Challenge Paths</h2>
        <p>No challenge paths have been defined yet.</p>
      </div>
    );
  }

  if (activeStage) {
    const challenge = activeStage.challenges[currentChallengeIndex];
    const isCurrentChallengeCompleted = isChallengeCompletedCheck(challenge.id);

    if (isCurrentChallengeCompleted) {
      const nextUncompletedIndex = activeStage.challenges.findIndex(
        (challenge, index) =>
          index > currentChallengeIndex &&
          !isChallengeCompletedCheck(challenge.id)
      );

      if (nextUncompletedIndex !== -1) {
        setCurrentChallengeIndex(nextUncompletedIndex);
        return null;
      } else {
        setActiveStage(null);
        return null;
      }
    }

    return (
      <div className="p-8 pb-24">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-white">
              {activeStage.title}
            </h2>
            <p className="text-gray-400">
              Challenge {currentChallengeIndex + 1} of{" "}
              {activeStage.challenges.length}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-400">
              {stageScore} pts
            </div>
            <div className="text-xs text-gray-400">Stage Score</div>
          </div>
        </div>

        <ChallengePlayer
          key={challenge.id}
          challenge={challenge}
          onComplete={handleChallengeComplete}
        />

        <div className="mt-6">
          <button
            onClick={() => setActiveStage(null)}
            className="px-4 py-2 bg-brand-teal hover:bg-brand-slate text-white rounded-lg transition-colors"
          >
            ← Back to Stages
          </button>
        </div>
      </div>
    );
  }

  const handleContinue = () => {
    setChallengeCompleted(false);
    setActiveStage(null);
    setCurrentChallengeIndex(0);
  };

  return (
    <div className="p-8 pb-24 pt-4 text-white">
      {isChallengeCompleted && (
        <FeedbackModal
          type={getFeedbackType(finalScore)}
          handleContinue={handleContinue}
          showConfetti={finalScore >= 100}
        >
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-white mb-2"
          >
            Stage Complete!
          </motion.h2>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className={`text-xl font-semibold mb-6 ${
              finalScore >= 100
                ? "text-green-400"
                : finalScore >= 50
                ? "text-yellow-400"
                : "text-gray-400"
            }`}
          >
            Your Score: {finalScore} points
          </motion.div>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-300 mb-4"
          >
            {finalScore >= 200
              ? "Legendary performance! You've mastered the psychological traps that destroy most traders."
              : finalScore >= 100
              ? "Amazing work! You've mastered the psychological traps that destroy most traders."
              : finalScore >= 50
              ? "Good progress! You're learning to overcome common trading psychology pitfalls."
              : "Keep practicing! Understanding trading psychology takes time to master."}
          </motion.p>
        </FeedbackModal>
      )}
      <h2 className="text-3xl font-bold mb-8">Legendary Challenge Paths</h2>

      <div className="flex flex-col lg:flex-row gap-4 mb-8 border-b border-white/10 pb-4">
        {challengePaths.map((path) => (
          <button
            key={path.id}
            onClick={() => setSelectedPath(path)}
            className={`px-4 py-2 rounded-lg cursor-pointer transition-colors ${
              selectedPath?.id === path.id
                ? "bg-brand-teal text-white"
                : "bg-gray-800 hover:bg-gray-700"
            }`}
          >
            {path.title}
          </button>
        ))}
      </div>

      {selectedPath && (
        <AnimatePresence>
          <motion.div
            key={selectedPath.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <h3 className="text-2xl font-bold mb-4">{selectedPath.title}</h3>
            <p className="mb-8 text-gray-400">{selectedPath.description}</p>
            <div className="space-y-4">
              {selectedPath.stages.map((stage) => {
                const completionStatus = getStageCompletionStatus(stage);
                const isClickable = !completionStatus.isFullyCompleted;

                return (
                  <motion.div
                    key={stage.id}
                    className={`p-6 rounded-lg border transition-colors ${
                      completionStatus.isFullyCompleted
                        ? "bg-green-500/10 border-green-500/30 cursor-not-allowed opacity-75"
                        : completionStatus.completed > 0
                        ? "bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 cursor-pointer"
                        : "bg-white/5 border-white/10 hover:bg-white/10 cursor-pointer"
                    }`}
                    onClick={() => isClickable && handleSelectStage(stage)}
                    whileHover={isClickable ? { scale: 1.02 } : {}}
                    whileTap={isClickable ? { scale: 0.98 } : {}}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-xl font-bold flex items-center gap-2">
                          {stage.title}
                          {completionStatus.isFullyCompleted && (
                            <span className="text-green-400">✓ Completed</span>
                          )}
                        </h4>
                        <p className="text-gray-400">{stage.description}</p>
                        {completionStatus.isFullyCompleted && (
                          <p className="text-sm text-green-400 mt-2">
                            All challenges in this stage have been completed!
                          </p>
                        )}
                        {completionStatus.completed > 0 &&
                          !completionStatus.isFullyCompleted && (
                            <p className="text-sm text-yellow-400 mt-2">
                              {completionStatus.total -
                                completionStatus.completed}{" "}
                              challenges remaining
                            </p>
                          )}
                      </div>
                      <div className="text-right ml-4">
                        <div
                          className={`text-sm font-semibold ${
                            completionStatus.isFullyCompleted
                              ? "text-green-400"
                              : completionStatus.completed > 0
                              ? "text-yellow-400"
                              : "text-gray-400"
                          }`}
                        >
                          {completionStatus.completed}/{completionStatus.total}
                        </div>
                        <div className="text-xs text-gray-400">completed</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
